// ============================================================================
// Wave 3.27 — Super-Admin Per-Company Backup & Restore Dialogs.
//
// Two dialogs, used from AdminCompanies.jsx:
//   * <CompanyBackupDialog />  — generate + download password-protected xlsx
//   * <CompanyRestoreDialog /> — upload encrypted xlsx and merge OR replace
// ============================================================================

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Download,
  Upload,
  ShieldAlert,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';

// ─────────────────────────────────────────────────────────────────────────
// Backup
// ─────────────────────────────────────────────────────────────────────────
export function CompanyBackupDialog({ company, open, onOpenChange }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [includeImages, setIncludeImages] = useState(true);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setIncludeImages(true);
    setBusy(false);
  };

  const passwordMismatch = password !== confirmPassword;
  const tooShort = password.length > 0 && password.length < 6;
  const canSubmit = password.length >= 6 && !passwordMismatch && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data, headers } = await api.post(
        `/admin/companies/${company.id}/backup`,
        { password, include_images: includeImages },
        { responseType: 'blob' },
      );
      // Extract filename from Content-Disposition (fallback: company-backup.xlsx)
      let filename = `${company.name || 'company'}-backup.xlsx`;
      const cd = headers?.['content-disposition'] || headers?.['Content-Disposition'];
      if (cd) {
        const m = /filename="([^"]+)"/.exec(cd);
        if (m) filename = m[1];
      }
      // Trigger download
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Backup downloaded — ${filename}`);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(extractError(e, 'Backup failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { if (!v) reset(); onOpenChange(v); } }}>
      <DialogContent className="max-w-md" data-testid="company-backup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-[var(--gold-icon)]" />
            Backup — {company?.name}
          </DialogTitle>
          <DialogDescription>
            Download a password-protected Excel file containing every record for this company. Keep the password safe — you will need it to restore the data later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="backup-password" className="text-xs">Backup Password</Label>
            <div className="relative">
              <Input
                id="backup-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                data-testid="backup-password-input"
                className="pr-9"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            {tooShort && (
              <div className="text-[11px] text-rose-600 mt-1">Password must be at least 6 characters.</div>
            )}
          </div>

          <div>
            <Label htmlFor="backup-password-confirm" className="text-xs">Confirm Password</Label>
            <Input
              id="backup-password-confirm"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter the password"
              data-testid="backup-password-confirm-input"
            />
            {confirmPassword.length > 0 && passwordMismatch && (
              <div className="text-[11px] text-rose-600 mt-1">Passwords don&apos;t match.</div>
            )}
          </div>

          <label className="inline-flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={includeImages}
              onCheckedChange={(v) => setIncludeImages(!!v)}
              data-testid="backup-include-images"
            />
            <span className="text-sm text-slate-700">Include jewellery images</span>
            <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600">
              Larger file
            </Badge>
          </label>
          <div className="text-[11px] text-slate-500 -mt-1 pl-7">
            Uncheck to skip large base64 image payloads and produce a smaller backup file.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="luxury-button"
            data-testid="backup-submit-button"
          >
            {busy ? <><Loader2 className="animate-spin mr-1.5" size={14} /> Generating…</> : <><Download size={14} className="mr-1.5" /> Generate & Download</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Restore
// ─────────────────────────────────────────────────────────────────────────
export function CompanyRestoreDialog({ company, open, onOpenChange, onRestored }) {
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState('merge'); // 'merge' | 'replace'
  const [confirmName, setConfirmName] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setPassword('');
    setShowPassword(false);
    setMode('merge');
    setConfirmName('');
    setBusy(false);
    setResult(null);
  };

  const canSubmit =
    !!file &&
    password.length >= 6 &&
    !busy &&
    (mode === 'merge' || confirmName.trim() === (company?.name || '').trim());

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('password', password);
      fd.append('mode', mode);
      if (mode === 'replace') fd.append('confirm_company_name', confirmName);
      const { data } = await api.post(
        `/admin/companies/${company.id}/restore`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setResult(data);
      toast.success(
        mode === 'replace'
          ? `Restore (replace) complete — ${Object.values(data.summary || {}).reduce((s, v) => s + (v.inserted || 0), 0)} rows inserted`
          : `Restore (merge) complete — ${Object.values(data.summary || {}).reduce((s, v) => s + (v.inserted || 0), 0)} rows inserted, ${Object.values(data.summary || {}).reduce((s, v) => s + (v.skipped || 0), 0)} skipped`,
      );
      onRestored?.();
    } catch (e) {
      toast.error(extractError(e, 'Restore failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { if (!v) reset(); onOpenChange(v); } }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="company-restore-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload size={18} className="text-[var(--diamond-icon)]" />
            Restore — {company?.name}
          </DialogTitle>
          <DialogDescription>
            Upload an encrypted Excel backup. You can either <strong>merge</strong> the backup into the current data (existing rows win) or <strong>replace</strong> all current data with the backup (destructive).
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-3">
            {/* File picker */}
            <div>
              <Label htmlFor="restore-file" className="text-xs">Backup file (.xlsx)</Label>
              <Input
                id="restore-file"
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="restore-file-input"
              />
              {file && (
                <div className="text-[11px] text-slate-500 mt-1">
                  Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </div>
              )}
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="restore-password" className="text-xs">Backup Password</Label>
              <div className="relative">
                <Input
                  id="restore-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password set at backup time"
                  data-testid="restore-password-input"
                  className="pr-9"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {/* Mode picker */}
            <div>
              <Label className="text-xs">Restore mode</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setMode('merge')}
                  className={`flex flex-col items-start gap-1 rounded-md border px-3 py-2 text-left transition-colors ${
                    mode === 'merge'
                      ? 'border-emerald-300 bg-emerald-50 ring-1 ring-emerald-200'
                      : 'border-[var(--divider)] hover:bg-slate-50'
                  }`}
                  data-testid="restore-mode-merge"
                >
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 size={14} className="text-emerald-600" />
                    <span className="font-medium text-sm">Merge</span>
                  </div>
                  <span className="text-[11px] text-slate-500">
                    Skip rows that already exist (existing data wins). Aggressive de-duplication.
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setMode('replace')}
                  className={`flex flex-col items-start gap-1 rounded-md border px-3 py-2 text-left transition-colors ${
                    mode === 'replace'
                      ? 'border-rose-300 bg-rose-50 ring-1 ring-rose-200'
                      : 'border-[var(--divider)] hover:bg-slate-50'
                  }`}
                  data-testid="restore-mode-replace"
                >
                  <div className="flex items-center gap-1.5">
                    <ShieldAlert size={14} className="text-rose-600" />
                    <span className="font-medium text-sm">Replace</span>
                  </div>
                  <span className="text-[11px] text-slate-500">
                    Wipe current data, restore from backup. Destructive — irreversible.
                  </span>
                </button>
              </div>
            </div>

            {/* Replace confirmation */}
            {mode === 'replace' && (
              <div className="rounded-md border border-rose-200 bg-rose-50/60 p-3 space-y-2">
                <div className="flex items-start gap-2 text-rose-800">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <div className="text-[12px] leading-snug">
                    <strong>Destructive:</strong> All current data for <strong>{company?.name}</strong> will be permanently deleted and replaced with the backup contents.
                    Type the company name exactly to confirm.
                  </div>
                </div>
                <Input
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder={`Type "${company?.name}" to confirm`}
                  data-testid="restore-confirm-name-input"
                />
              </div>
            )}
          </div>
        )}

        {/* Result panel */}
        {result && (
          <div className="space-y-3" data-testid="restore-result-panel">
            <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3">
              <div className="flex items-center gap-2 text-emerald-800 font-medium text-sm">
                <CheckCircle2 size={14} />
                Restore complete ({result.mode})
              </div>
              <div className="text-[11px] text-slate-600 mt-1">
                Source: {result.metadata?.source_company_name} ·{' '}
                generated {result.metadata?.generated_at_ist?.slice(0, 19) || '—'} ·{' '}
                by {result.metadata?.generated_by_email}
              </div>
            </div>
            <div className="border border-[var(--divider)] rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-2 py-1.5">Collection</th>
                    <th className="text-right px-2 py-1.5">Inserted</th>
                    <th className="text-right px-2 py-1.5">Skipped</th>
                    <th className="text-right px-2 py-1.5">Deleted</th>
                  </tr>
                </thead>
                <tbody data-testid="restore-summary-body">
                  {Object.entries(result.summary || {}).map(([k, v]) => (
                    <tr key={k} className="border-t border-[var(--divider)]">
                      <td className="px-2 py-1.5 font-mono">{k}</td>
                      <td className={`px-2 py-1.5 text-right font-mono tabular-nums ${v.inserted > 0 ? 'text-emerald-700 font-medium' : 'text-slate-400'}`}>{v.inserted ?? 0}</td>
                      <td className={`px-2 py-1.5 text-right font-mono tabular-nums ${v.skipped > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{v.skipped ?? 0}</td>
                      <td className={`px-2 py-1.5 text-right font-mono tabular-nums ${v.deleted > 0 ? 'text-rose-700' : 'text-slate-400'}`}>{v.deleted ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(result.warnings || []).length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 text-[11px] text-amber-800 space-y-1" data-testid="restore-warnings">
                <div className="flex items-center gap-1.5 font-medium">
                  <AlertTriangle size={12} /> Post-restore validation warnings
                </div>
                {result.warnings.map((w, i) => (
                  <div key={i} className="pl-4 font-mono">• {w}</div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={() => onOpenChange(false)} className="luxury-button" data-testid="restore-close-button">Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
              <Button
                onClick={submit}
                disabled={!canSubmit}
                className={mode === 'replace' ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'luxury-button'}
                data-testid="restore-submit-button"
              >
                {busy ? <><Loader2 className="animate-spin mr-1.5" size={14} /> Restoring…</> : <><Upload size={14} className="mr-1.5" /> {mode === 'replace' ? 'Wipe & Restore' : 'Merge & Restore'}</>}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Wave 3.28 — Restore into a NEW Company
// ─────────────────────────────────────────────────────────────────────────
export function CompanyRestoreAsNewDialog({ open, onOpenChange, onRestored }) {
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [newName, setNewName] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [newGstin, setNewGstin] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setPassword('');
    setShowPassword(false);
    setNewName('');
    setNewAddress('');
    setNewGstin('');
    setNewPhone('');
    setNewEmail('');
    setBusy(false);
    setResult(null);
  };

  React.useEffect(() => {
    if (open) reset();
     
  }, [open]);

  const canSubmit = !!file && password.length >= 6 && !!newName.trim() && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('password', password);
      fd.append('new_company_name', newName.trim());
      if (newAddress.trim()) fd.append('new_company_address', newAddress.trim());
      if (newGstin.trim()) fd.append('new_company_gstin', newGstin.trim());
      if (newPhone.trim()) fd.append('new_company_phone', newPhone.trim());
      if (newEmail.trim()) fd.append('new_company_email', newEmail.trim());
      // No users — SA-only access by default.
      fd.append('new_company_users_json', '');
      const { data } = await api.post(
        '/admin/companies/restore-as-new',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setResult(data);
      toast.success(`New company "${data.new_company_name}" created from backup`);
      onRestored?.();
    } catch (e) {
      toast.error(extractError(e, 'Restore-as-new failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="restore-as-new-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload size={18} className="text-[var(--ink-navy)]" />
            Restore into new company
          </DialogTitle>
          <DialogDescription>
            Upload an encrypted backup and import it into a brand-new company. The backup&apos;s users
            list is <strong>not</strong> imported — access defaults to Super Admin only. You can
            assign company users from the User Management screen later.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="ran-file" className="text-xs">Backup file (.xlsx)</Label>
              <Input
                id="ran-file"
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="ran-file-input"
              />
              {file && (
                <div className="text-[11px] text-slate-500 mt-1">
                  Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="ran-password" className="text-xs">Backup Password</Label>
              <div className="relative">
                <Input
                  id="ran-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password set at backup time"
                  data-testid="ran-password-input"
                  className="pr-9"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            <div className="border-t border-[var(--divider)] pt-3 space-y-2">
              <div className="text-xs font-medium text-slate-700">New company profile</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <Label className="text-xs">Name *</Label>
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="e.g. Neutral Tag Domain Archive"
                    data-testid="ran-new-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">GSTIN</Label>
                  <Input
                    value={newGstin}
                    onChange={(e) => setNewGstin(e.target.value)}
                    data-testid="ran-new-gstin"
                  />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    data-testid="ran-new-phone"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Email</Label>
                  <Input
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    data-testid="ran-new-email"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Address</Label>
                  <Input
                    value={newAddress}
                    onChange={(e) => setNewAddress(e.target.value)}
                    data-testid="ran-new-address"
                  />
                </div>
              </div>
              <div className="text-[11px] text-slate-500 italic">
                If left blank, address/GSTIN/phone/email will fall back to the values from the backup.
              </div>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3" data-testid="ran-result-panel">
            <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3">
              <div className="flex items-center gap-2 text-emerald-800 font-medium text-sm">
                <CheckCircle2 size={14} />
                New company &ldquo;{result.new_company_name}&rdquo; created
              </div>
              <div className="text-[11px] text-slate-600 mt-1">
                Source backup: {result.metadata?.source_company_name} ·{' '}
                generated {result.metadata?.generated_at_ist?.slice(0, 19) || '—'} ·{' '}
                by {result.metadata?.generated_by_email}
              </div>
              <div className="text-[11px] text-slate-600">
                Users created: {result.users_created || 0} · linked existing: {result.users_linked_existing || 0}
              </div>
            </div>
            <div className="border border-[var(--divider)] rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-2 py-1.5">Collection</th>
                    <th className="text-right px-2 py-1.5">Inserted</th>
                    <th className="text-right px-2 py-1.5">Skipped</th>
                  </tr>
                </thead>
                <tbody data-testid="ran-summary-body">
                  {Object.entries(result.summary || {}).map(([k, v]) => (
                    <tr key={k} className="border-t border-[var(--divider)]">
                      <td className="px-2 py-1.5 font-mono">{k}</td>
                      <td className={`px-2 py-1.5 text-right font-mono tabular-nums ${v.inserted > 0 ? 'text-emerald-700 font-medium' : 'text-slate-400'}`}>{v.inserted ?? 0}</td>
                      <td className={`px-2 py-1.5 text-right font-mono tabular-nums ${(v.skipped || v.skipped_users_sheet) > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{v.skipped ?? v.skipped_users_sheet ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(result.warnings || []).length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 text-[11px] text-amber-800 space-y-1" data-testid="ran-warnings">
                <div className="flex items-center gap-1.5 font-medium">
                  <AlertTriangle size={12} /> Post-restore validation warnings
                </div>
                {result.warnings.map((w, i) => (
                  <div key={i} className="pl-4 font-mono">• {w}</div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={() => onOpenChange(false)} className="luxury-button" data-testid="ran-close-button">
              Close
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={!canSubmit}
                className="luxury-button"
                data-testid="ran-submit-button"
              >
                {busy ? (
                  <>
                    <Loader2 className="animate-spin mr-1.5" size={14} /> Creating…
                  </>
                ) : (
                  <>
                    <Upload size={14} className="mr-1.5" /> Restore as new
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
